<template>
    <div>
      <div class="air__utils__heading">
        <div class="row">
          <div class="col-4 col-sm-6">
            <h5>Books</h5>
          </div>
          <div class="col-8 col-sm-6 text-right">
            <div class="d-inline-flex flex-row">
              <div class="mr-2">
                <bulk-upload-book v-if="pagepermission.insertable"/>
              </div>
              <div class="mr-2">
                <new-book v-if="pagepermission.insertable" @created="handleCreated"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row justify-content-center" v-if="pagepermission.viewable">
        <div class="col-xl-12">
          <div class="row align-items-center">
            <div class="col-12 col-md-6 mb-2" v-if="!selectedRowKeys.length">
              <a-input-search
                placeholder="Search Book..."
                size="large"
                v-model="search"
                @search="searchBook"
              />
            </div>

            <div class="col-12 col-md-6 mb-2" v-if="!selectedRowKeys.length">
              <div class="row justify-content-around">
                <div class="col">
                  <a-select
                    size="large"
                    style="width: 100%"
                    placeholder="All Classes"
                    optionFilterProp="children"
                    v-model="selectedClassId"
                    :filterOption="false"
                    :defaultValue="undefined"
                    @change="handleClassSelect"
                  >
                    <a-select-option :value="undefined"
                    >All Classes</a-select-option
                    >
                    <template v-for="schoolClass in schoolClasses">
                      <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                        {{schoolClass.name}}
                      </a-select-option>
                    </template>
                  </a-select>
                </div>

                <div class="col">
                  <a-select
                    size="large"
                    style="width: 100%"
                    placeholder="All Categories"
                    optionFilterProp="children"
                    v-model="selectedCategoryId"
                    :filterOption="false"
                    :defaultValue="undefined"
                    @change="handleCategorySelect"
                  >
                    <a-select-option :value="undefined">
                      All Categories
                    </a-select-option>
                    <template v-for="category in categories">
                      <a-select-option :value="category.id" :key="category.id">
                        {{category.name}}
                      </a-select-option>
                    </template>
                  </a-select>
                </div>

                <div class="col">
                  <a-select
                    size="large"
                    style="width: 100%"
                    placeholder="All Sub Categories"
                    optionFilterProp="children"
                    v-model="selectedSubCategoryId"
                    :filterOption="false"
                    :defaultValue="undefined"
                    @change="handleSubCategorySelect"
                  >
                    <a-select-option :value="undefined">
                      All Sub Categories
                    </a-select-option>
                    <template v-for="subCategory in subCategories">
                      <a-select-option :value="subCategory.id" :key="subCategory.id">
                        {{subCategory.name}}
                      </a-select-option>
                    </template>
                  </a-select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row justify-content-center" v-if="pagepermission.viewable">
        <div class="col-xl-12">
          <div class="card">
            <div class="card-body">
              <books-table :data="data" :rolesetting="pagepermission" :loading="loading" @updateTable="handleUpdateTable" />
            </div>
          </div>
          <div class="text-right">
            <a-pagination
                showSizeChanger
                @showSizeChange="onShowSizeChange"
                :total="meta.total"
                :pageSize.sync="pageSize"
                v-model="current"
                @change="onChange"
              >
            </a-pagination>
          </div>
        </div>
      </div>
      <div class="row justify-content-center" v-else>
        <template>
        <div>
          <a-spin :spinning="permissionCheckSpinning">
            <p class="error" v-if="!permissionCheckSpinning">
              You don't have permission to use this feature.
            </p>
          </a-spin>
        </div>
      </template>
      </div>
    </div>
  </template>

<script>
import BooksTable from '@/components/library/books/index'
import NewBook from '@/components/library/books/new'
import BulkUploadBook from '@/components/library/books/Bulk/index'
import { debounce } from 'lodash'

export default {
  components: {
    BooksTable,
    NewBook,
    BulkUploadBook,
  },
  beforeMount: function() {
    this.permissionCheckSpinning = true
    let rc = this.$roleutility.checkpermission('library', 22)
    rc.then(data => {
      // console.log('RoleCheck!Data processed. ', data)
      this.pagepermission = data.pagePermission
      this.permissionCheckSpinning = false
    })
  },
  created() {
    this.searchBook = debounce(this.searchBook, 500)
  },
  mounted: function() {
    this.getBooks()
    this.getClasses()
    this.getBookCategories()
  },
  data: function() {
    return {
      loading: false,
      visible: false,
      data: [],
      meta: {},
      current: 1,
      pageSize: 30,
      search: null,
      selectedRowKeys: [],
      schoolClasses: [],
      allCategories: [], // All categories including sub categories
      categories: [], // Only the main categories i.e. without subcategories
      subCategories: [],
      selectedClassId: undefined,
      selectedCategoryId: undefined,
      selectedSubCategoryId: undefined,
      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
      },
      permissionCheckSpinning: true,
    }
  },
  methods: {
    handleTableSelect: function(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },
    getClasses: function() {
      this.$http.get('/api/class')
        .then(({ data }) => {
          this.schoolClasses = data.data
        })
        .catch(error => {
          console.log(error)
        })
    },
    getBookCategories: function() {
      this.$http.get('/api/library/category')
        .then(({ data }) => {
          this.allCategories = data.data
          this.categories = this.allCategories.filter((category) => category.parent_id === null)
        })
        .catch(error => {
          console.log(error)
        })
    },
    getSubCategories: function(selectedCategoryId) {
      this.subCategories = this.allCategories.filter((category) => category.parent_id === selectedCategoryId)
    },
    getBooks: function(isSearch) {
      this.selectedRowKeys = []
      this.loading = true

      let page = this.current
      if (isSearch) {
        page = 1
        this.current = 1
      }

      // this.$http.get('/api/inventory/product/stock/all', {
      this.$http.get('/api/library/books', {
        params: {
          class_id: this.selectedClassId,
          category_id: this.selectedCategoryId,
          sub_category_id: this.selectedSubCategoryId,
          limit: this.pageSize,
          page: page,
          search: this.search,
        },
      }).then(({ data }) => {
        this.loading = false
        this.data = data.data
        this.meta = data.meta
      })
    },
    searchBook: function(value) {
      this.search = value
      this.getBooks(true)
    },
    onChangeSearchBooks: function(e) {
      this.search = e.target.value
      this.getBooks(true)
    },
    handleCreated: function() {
      this.getBooks()
    },
    handleUpdateTable: function() {
      this.getBooks()
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.getBooks()
    },
    onChange(current) {
      this.current = current
      this.getBooks()
    },
    handleClassSelect(selectedClassId) {
      this.getBooks(true)
    },
    handleCategorySelect(selectedCategoryId) {
      this.getBooks(true)
      this.getSubCategories(selectedCategoryId)
    },
    handleSubCategorySelect(selectedSubCategoryId) {
      this.getBooks(true)
    },
  },
}
</script>
  <style>
  .error { padding: 25px 5px; }
  </style>
